"use client";

import { trackEventWithAttribution } from "@/lib/analytics/posthog-helpers";
import {
  GUIDED_COHORT_COURSE_FORMAT,
  GUIDED_COHORT_LANGUAGES,
  type GuidedCohortLanguageSlug,
  getRollingCohortWindow,
} from "@/lib/courses/rolling-cohorts";
import { CalendarDays } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useMemo, useRef, useState } from "react";

type ReservationStatus = "idle" | "submitting" | "success" | "error";

const COURSE_FORMAT = [
  { label: "Duration", value: `${GUIDED_COHORT_COURSE_FORMAT.durationWeeks} weeks` },
  {
    label: "Led by a human teacher",
    value: `${GUIDED_COHORT_COURSE_FORMAT.liveClassCount} live classes`,
  },
  {
    label: "Private AI practice",
    value: `${GUIDED_COHORT_COURSE_FORMAT.dailyPracticeMinutes} min/day`,
  },
  {
    label: "Total guided learning",
    value: `${GUIDED_COHORT_COURSE_FORMAT.totalGuidedHours} hours`,
  },
];

function createIdempotencyKey() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `cohort-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function CohortOfferCard({
  defaultLanguage,
  email,
  firstName,
  onAuthRequired,
  source = "paywall",
}: {
  defaultLanguage: GuidedCohortLanguageSlug;
  email: string;
  firstName?: string | null;
  onAuthRequired?: () => void;
  source?: string;
}) {
  const posthog = usePostHog();
  const cohortWindow = useMemo(() => getRollingCohortWindow(), []);
  const [languageSlug, setLanguageSlug] = useState<GuidedCohortLanguageSlug>(defaultLanguage);
  const [status, setStatus] = useState<ReservationStatus>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const idempotencyKey = useRef<string | null>(null);

  const selectedLanguage =
    GUIDED_COHORT_LANGUAGES.find((language) => language.slug === languageSlug) ??
    GUIDED_COHORT_LANGUAGES[0];

  async function reservePriorityPlace() {
    if (status === "submitting") return;
    if (!email) {
      onAuthRequired?.();
      return;
    }

    setStatus("submitting");
    setErrorMessage("");
    idempotencyKey.current ||= createIdempotencyKey();
    trackEventWithAttribution(posthog, "guided_cohort_reservation_started", {
      course_language: selectedLanguage.name,
      course_language_slug: languageSlug,
      preferred_format: "online",
      target_start_month: cohortWindow.targetStartMonth,
      source,
    });

    try {
      const sourcePath = window.location.pathname.slice(0, 300);
      const response = await fetch("/api/courses/cohort-reservations", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          idempotencyKey: idempotencyKey.current,
          languageSlug,
          firstName,
          email,
          website: "",
          sourcePath,
        }),
      });
      const result = await response.json().catch(() => null);

      if (!response.ok || result?.ok !== true || typeof result.id !== "string") {
        throw new Error(`Cohort reservation failed with status ${response.status}`);
      }

      setStatus("success");
      trackEventWithAttribution(posthog, "guided_cohort_reserved", {
        course_language: selectedLanguage.name,
        course_language_slug: languageSlug,
        preferred_format: "online",
        target_start_month: result.targetStartMonth,
        source,
      });
    } catch (error) {
      setStatus("error");
      setErrorMessage("We could not save your priority place. Please try again.");
      trackEventWithAttribution(posthog, "guided_cohort_reservation_failed", {
        course_language_slug: languageSlug,
        failure_type: error instanceof Error ? "request_failed" : "unknown",
        source,
      });
    }
  }

  return (
    <article className="flex h-full flex-col rounded-2xl border border-white/15 bg-[#15161B] p-6 md:p-8">
      <header className="md:min-h-32">
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#FD954F]">
          Human-led course
        </p>
        <h3 className="mt-3 text-2xl font-bold text-white">Human Teacher Course</h3>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-[#AEAEB2]">
          A human teacher leads six live online classes. You practise privately with ChickyTutor for
          30 minutes every day.
        </p>
      </header>

      <section className="mt-6">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-4xl font-bold text-white">€300</span>
          <span className="text-sm text-[#AEAEB2]">for the complete course</span>
        </div>
        <p className="mt-2 text-xs text-[#AEAEB2]">
          No payment until your teacher, class dates, and group are confirmed.
        </p>
        <p className="mt-3 flex items-center gap-2 text-sm font-semibold text-white/80">
          <CalendarDays className="h-4 w-4 text-[#FD954F]" />
          Next cohort forming for {cohortWindow.targetStartLabel}
        </p>
      </section>

      <div className="mt-6 border-t border-white/10 pt-5">
        <label className="grid gap-2 text-sm font-semibold text-white/85">
          Course language
          <select
            className="min-h-11 rounded-lg border border-white/10 bg-[#0F0F14] px-3 py-2.5 text-white outline-none transition-colors focus:border-[#FD954F] focus:ring-1 focus:ring-[#FD954F]"
            disabled={status === "submitting" || status === "success"}
            onChange={(event) => {
              const nextLanguage = event.target.value as GuidedCohortLanguageSlug;
              setLanguageSlug(nextLanguage);
              idempotencyKey.current = null;
              trackEventWithAttribution(posthog, "guided_cohort_language_selected", {
                course_language_slug: nextLanguage,
                source,
              });
            }}
            value={languageSlug}
          >
            {GUIDED_COHORT_LANGUAGES.map((language) => (
              <option key={language.slug} value={language.slug}>
                {language.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <section className="my-6 border-t border-white/10 pt-5">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-white/70">
            Course format
          </p>
          <p className="text-xs font-semibold text-[#FD954F]">
            {GUIDED_COHORT_COURSE_FORMAT.levelLabel}
          </p>
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-white/10 bg-white/10">
          {COURSE_FORMAT.map((item) => (
            <div className="bg-[#0F0F14] p-3" key={item.label}>
              <dt className="text-xs leading-5 text-[#AEAEB2]">{item.label}</dt>
              <dd className="mt-1 text-sm font-bold text-white">{item.value}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-3 text-xs text-[#AEAEB2]">
          {GUIDED_COHORT_COURSE_FORMAT.modalityLabel} · {GUIDED_COHORT_COURSE_FORMAT.groupLabel} ·
          AI practice included
        </p>
      </section>

      {status === "success" ? (
        <div className="mt-auto rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 text-sm text-emerald-100">
          <p className="font-bold">You&apos;re on the priority list</p>
          <p className="mt-1 text-emerald-200/90">
            No payment was taken. We will contact you when your {selectedLanguage.name} teacher,
            online class dates, and group are confirmed.
          </p>
        </div>
      ) : (
        <>
          {status === "error" && (
            <p className="mb-3 text-sm text-red-300" role="alert">
              {errorMessage}
            </p>
          )}
          <button
            className="mt-auto min-h-12 w-full rounded-xl bg-[#FD954F] px-4 py-3 text-sm font-bold text-[#1C1C1E] transition-colors hover:bg-[#F28747] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F0F14] disabled:cursor-not-allowed disabled:opacity-60"
            disabled={(!email && !onAuthRequired) || status === "submitting"}
            onClick={reservePriorityPlace}
            type="button"
          >
            {status === "submitting" ? "Saving your place…" : "Join the priority list (no payment)"}
          </button>
        </>
      )}
    </article>
  );
}
